import React, { useState, useEffect, useCallback } from 'react';
import { IDadosEnderecoDemanda, BlocoEnderecoProps, ViaCepResponse } from '../types';
import {
  Container,
  Header,
  HeaderIndicator,
  Title,
  FormGrid,
  ThreeColumnRow,
  TwoColumnRow,
  FieldContainer,
  Label,
  InputContainer,
  Input,
  LoadingSpinner,
  ErrorMessage
} from './styles';
import { FormSectionTitle } from '../../styles';


const BlocoEndereco: React.FC<BlocoEnderecoProps> = ({
  erros,
  setDadosEndereco,
  dadosEndereco,
  modalidade,
  enderecoVirtual,
  onChangeEnderecoVirtual,
  onEstadoComarcaChange
}) => {

  const [loadingCep, setLoadingCep] = useState(false);
  const [erroCep, setErroCep] = useState<string>('');
  const [errosLocais, setErrosLocais] = useState<any>({});

  // Limpa erros locais quando os erros externos mudarem
  useEffect(() => {
    if (Object.keys(erros).length === 0) {
      setErrosLocais({});
    }
  }, [erros]);

  // Função para validar CEP
  const validarCep = useCallback((cep: string) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo && cepLimpo.length !== 8) {
      return 'CEP deve ter 8 dígitos';
    }
    return '';
  }, []);

  // Função para validar campo obrigatório (removida - campos são opcionais)

  // Função para validar estado (UF) - removida pois não é mais obrigatório

  // Função para validar número
  const validarNumero = useCallback((numero: string) => {
    if (numero.trim() && !/^\d+$/.test(numero.trim())) {
      return 'Número deve conter apenas dígitos';
    }
    return '';
  }, []);

  // Função para buscar dados do CEP na API do ViaCEP
  const buscarCep = useCallback(async (cep: string) => {
    // Remove caracteres não numéricos
    const cepLimpo = cep.replace(/\D/g, '');
    
    // Valida se o CEP tem 8 dígitos
    if (cepLimpo.length !== 8) {
      setErroCep('');
      return;
    }

    setLoadingCep(true);
    setErroCep('');

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data: ViaCepResponse = await response.json();

      if (data.erro) {
        setErroCep('CEP não encontrado. Verifique o CEP digitado.');
        return;
      }

      const novosDados = {
        ...dadosEndereco,
        cep: cepLimpo,
        rua: data.logradouro || '',
        bairro: data.bairro || '',
      };

      setDadosEndereco(novosDados);

      // Atualiza Estado/Comarca através do callback se disponível e se tiver dados
      if (onEstadoComarcaChange && data.uf && data.localidade) {
        onEstadoComarcaChange(data.uf, data.localidade);
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setErroCep('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setLoadingCep(false);
    }
  }, [dadosEndereco, setDadosEndereco, onEstadoComarcaChange]);

  const formatarCep = (cep: string) => {
    if (!cep) return '';
    const cepLimpo = cep.replace(/\D/g, '');
    return cepLimpo.length === 8 ? cepLimpo.replace(/(\d{5})(\d{3})/, '$1-$2') : cepLimpo;
  };

  // Função para validar campo específico
  const validarCampo = useCallback((campo: keyof IDadosEnderecoDemanda, valor: string) => {
    let erro = '';
    
    switch (campo) {
      case 'cep':
        erro = validarCep(valor);
        break;
      case 'numero':
        erro = validarNumero(valor);
        break;
      case 'rua':
      case 'bairro':
      case 'complemento':
        // Campos opcionais não precisam de validação
        break;
    }
    
    return erro;
  }, [validarCep, validarNumero]);

  // Função para lidar com mudanças nos campos
  const handleInputChange = useCallback((campo: keyof IDadosEnderecoDemanda, valor: string) => {
    const novosDados = {
      ...dadosEndereco,
      [campo]: valor
    };

    setDadosEndereco(novosDados);

    // Validação em tempo real
    const erro = validarCampo(campo, valor);
    setErrosLocais((prev: any) => ({
      ...prev,
      [campo]: erro
    }));

    // Se o campo alterado foi o CEP, limpa erro anterior
    if (campo === 'cep') {
      setErroCep('');
    }
  }, [dadosEndereco, setDadosEndereco, validarCampo]);

  // Função para lidar com blur no campo CEP
  const handleCepBlur = useCallback(() => {
    const cepLimpo = (dadosEndereco.cep || '').replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      buscarCep(cepLimpo);
    }
  }, [dadosEndereco.cep, buscarCep]);

  return (
    <Container>
      {/* Header do Bloco */}
      <FormSectionTitle>Endereço</FormSectionTitle>

      {/* Formulário de Endereço */}
      <FormGrid>
        {/* Linha 1: CEP (10%), Rua (35%), Bairro (35%) */}
        <ThreeColumnRow>
          <FieldContainer className='cep'>
            <Label>CEP</Label>
            <InputContainer>
              <Input
                type="text"
                value={formatarCep(dadosEndereco.cep || '')}
                onChange={(e) => handleInputChange('cep', e.target.value)}
                onBlur={handleCepBlur}
                placeholder="00000-000"
                maxLength={9}
                hasError={!!(erros.cep || erroCep || errosLocais.cep)}
              />
              {loadingCep && <LoadingSpinner />}
            </InputContainer>
            {(erros.cep || erroCep || errosLocais.cep) && (
              <ErrorMessage>{erros.cep || erroCep || errosLocais.cep}</ErrorMessage>
            )}
          </FieldContainer>

          <FieldContainer className='rua'>
            <Label>Rua</Label>
            <Input
              type="text"
              value={dadosEndereco.rua || ''}
              onChange={(e) => handleInputChange('rua', e.target.value)}
              placeholder="Rua, Avenida, etc."
              hasError={!!(erros.rua || errosLocais.rua)}
            />
            {(erros.rua || errosLocais.rua) && (
              <ErrorMessage>{erros.rua || errosLocais.rua}</ErrorMessage>
            )}
          </FieldContainer>

          <FieldContainer className='bairro'>
            <Label>Bairro</Label>
            <Input
              type="text"
              value={dadosEndereco.bairro || ''}
              onChange={(e) => handleInputChange('bairro', e.target.value)}
              placeholder="Nome do bairro"
              hasError={!!(erros.bairro || errosLocais.bairro)}
            />
            {(erros.bairro || errosLocais.bairro) && (
              <ErrorMessage>{erros.bairro || errosLocais.bairro}</ErrorMessage>
            )}
          </FieldContainer>
        </ThreeColumnRow>

        {/* Linha 2: Número (5%), Complemento (até 60%) e Endereço Virtual (aparece quando modalidade = Virtual) */}
        <TwoColumnRow>
          <FieldContainer className='numero'>
            <Label>Número</Label>
            <Input
              type="text"
              value={dadosEndereco.numero || ''}
              onChange={(e) => handleInputChange('numero', e.target.value)}
              placeholder="123"
              hasError={!!(erros.numero || errosLocais.numero)}
            />
            {(erros.numero || errosLocais.numero) && (
              <ErrorMessage>{erros.numero || errosLocais.numero}</ErrorMessage>
            )}
          </FieldContainer>

          <FieldContainer className='complemento'>
            <Label>Complemento</Label>
            <Input
              type="text"
              value={dadosEndereco.complemento || ''}
              onChange={(e) => handleInputChange('complemento', e.target.value)}
              placeholder="Apartamento, sala, etc."
              hasError={false}
            />
          </FieldContainer>
          {/** Campo Endereço Virtual (renderizado externamente via props) ficará aqui quando for Virtual **/}
          { (modalidade === 'Virtual' || modalidade === 'online') && (
            <FieldContainer className='endereco-virtual'>
              <Label>Endereço virtual</Label>
              <Input
                type="text"
                value={enderecoVirtual || ''}
                onChange={(e) => onChangeEnderecoVirtual && onChangeEnderecoVirtual(e.target.value)}
                placeholder="Link/ID da sala virtual"
                hasError={false}
              />
            </FieldContainer>
          )}
        </TwoColumnRow>
      </FormGrid>
    </Container>
  );
};

export default BlocoEndereco;
